import type { RuntimeServicePricing } from "@/features/pricing/runtimePricing";

const DEFAULT_SELECTION_ENHANCE_MAX_WORDS = 600;
const DEFAULT_SELECTION_ENHANCE_MAX_CREDIT_COST = 2;
const DEFAULT_CHAPTER_POLISH_MIN_CREDIT_COST = 3;
const DEFAULT_CHAPTER_POLISH_WORDS_PER_CREDIT = 300;

type SelectionEnhancePricingConfig =
  | Pick<RuntimeServicePricing, "creditCost">
  | number
  | null
  | undefined;

type ChapterPolishPricingConfig =
  | Partial<Pick<RuntimeServicePricing, "creditCost" | "minimumCreditCost">>
  | number
  | null
  | undefined;

function toNonNegativeInteger(value: number | null | undefined, fallback: number) {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.trunc(value)
    : fallback;
}

function toPositiveInteger(value: number | null | undefined, fallback: number) {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.trunc(value)
    : fallback;
}

export function calculateSelectionEnhanceCreditCost(
  wordCount: number | null | undefined,
  config: SelectionEnhancePricingConfig,
) {
  const maxCreditCost =
    typeof config === "number"
      ? toNonNegativeInteger(config, DEFAULT_SELECTION_ENHANCE_MAX_CREDIT_COST)
      : toNonNegativeInteger(config?.creditCost, DEFAULT_SELECTION_ENHANCE_MAX_CREDIT_COST);
  if (maxCreditCost === 0) {
    return 0;
  }

  const normalizedWordCount = toPositiveInteger(wordCount, 1);
  const billableWordCount = Math.min(
    normalizedWordCount,
    DEFAULT_SELECTION_ENHANCE_MAX_WORDS,
  );

  return Math.max(
    1,
    Math.ceil(
      (billableWordCount / DEFAULT_SELECTION_ENHANCE_MAX_WORDS) * maxCreditCost,
    ),
  );
}

export function calculateChapterPolishCreditCost(
  wordCount: number | null | undefined,
  config: ChapterPolishPricingConfig,
) {
  const minimumCreditCost =
    typeof config === "number"
      ? toNonNegativeInteger(config, DEFAULT_CHAPTER_POLISH_MIN_CREDIT_COST)
      : toNonNegativeInteger(
        config?.minimumCreditCost ?? config?.creditCost,
        DEFAULT_CHAPTER_POLISH_MIN_CREDIT_COST,
      );
  if (minimumCreditCost === 0) {
    return 0;
  }

  const normalizedWordCount = toPositiveInteger(wordCount, 1);

  return Math.max(
    minimumCreditCost,
    Math.ceil(normalizedWordCount / DEFAULT_CHAPTER_POLISH_WORDS_PER_CREDIT),
  );
}
